import type { Pass, PassResult, EnsembleInput } from './types'

// Deterministic pass_id: template|model|top_k|temperature hashed to 12 chars
function makePassId(template: string, model: string, top_k: number, temp: number): string {
  const str = `${template}|${model}|${top_k}|${temp}`
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash).toString(36).padStart(8, '0').slice(0, 12)
}

export const ALL_PASSES: Pass[] = ([
  { template: 'EVIDENCE_GROUNDED', model: 'demo-deterministic', top_k: 10, temperature: 0.0, pass_id: '' },
  { template: 'SEGMENT_RESPONSE', model: 'demo-deterministic', top_k: 8, temperature: 0.1, pass_id: '' },
  { template: 'SKEPTICAL_RESEARCHER', model: 'demo-deterministic', top_k: 6, temperature: 0.2, pass_id: '' },
  { template: 'BEHAVIORAL_DRIVER', model: 'demo-deterministic', top_k: 8, temperature: 0.1, pass_id: '' },
  { template: 'COUNTERFACTUAL_RISK', model: 'demo-deterministic', top_k: 6, temperature: 0.15, pass_id: '' },
  { template: 'QUANTITATIVE_DIST', model: 'demo-deterministic', top_k: 10, temperature: 0.0, pass_id: '' },
  { template: 'VALIDATION_PLANNER', model: 'demo-deterministic', top_k: 5, temperature: 0.0, pass_id: '' },
] as Pass[]).map(p => ({ ...p, pass_id: makePassId(p.template, p.model, p.top_k, p.temperature) }))

// Deterministic demo runner for each pass template
export function runPass(pass: Pass, input: EnsembleInput): PassResult {
  const stimuliLabels = input.stimuli.map(s => s.label)
  const primaryWinner = input.stimuli[1]?.label ?? input.stimuli[0]?.label ?? 'Option A'
  const openTexts = input.evidence_rows.filter(r => r.open_text).map(r => r.open_text!)

  // Seeded distribution: B wins ~58%, A ~32%, C ~10%
  const baseDistribution: Record<string, number> = {}
  const shares = [0.58, 0.32, 0.10]
  stimuliLabels.forEach((label, i) => {
    baseDistribution[label] = shares[i] ?? 0.05
  })

  switch (pass.template) {
    case 'EVIDENCE_GROUNDED':
      return {
        pass_id: pass.pass_id,
        template: pass.template,
        predicted_winner: primaryWinner,
        predicted_distribution: baseDistribution,
        reasoning: `Based on ${input.evidence_rows.length} evidence rows, ${primaryWinner} shows consistent preference signals across the segment. Key evidence: respondents cite concrete benefit language and trust cues.`,
        evidence_cited: openTexts.slice(0, 3),
      }

    case 'SEGMENT_RESPONSE':
      return {
        pass_id: pass.pass_id,
        template: pass.template,
        predicted_winner: primaryWinner,
        predicted_distribution: baseDistribution,
        reasoning: `Segment echo for "${input.segment_name}" predicts ${primaryWinner} resonates most strongly. Historical segment data shows preference for direct, evidence-backed messaging.`,
        evidence_cited: openTexts.slice(1, 4),
      }

    case 'SKEPTICAL_RESEARCHER':
      // Skeptical pass sometimes dissents — uses first stimulus instead
      const skepticWinner = input.stimuli[0]?.label ?? primaryWinner
      const skepticDist: Record<string, number> = {}
      stimuliLabels.forEach((label, i) => {
        skepticDist[label] = i === 0 ? 0.48 : i === 1 ? 0.40 : 0.12
      })
      return {
        pass_id: pass.pass_id,
        template: pass.template,
        predicted_winner: skepticWinner,
        predicted_distribution: skepticDist,
        reasoning: `Skeptical review: while ${primaryWinner} performs well on general population, evidence for the "${input.segment_name}" sub-segment is thin (only ${Math.min(12, input.evidence_rows.length)} relevant open-end responses). Recommend validation.`,
        evidence_cited: openTexts.slice(0, 2),
        dissent_notes: `Challenges ${primaryWinner} lead for this segment due to low open-end evidence depth`,
      }

    case 'BEHAVIORAL_DRIVER':
      return {
        pass_id: pass.pass_id,
        template: pass.template,
        predicted_winner: primaryWinner,
        predicted_distribution: baseDistribution,
        reasoning: `Behavioral analysis: ${primaryWinner} triggers trust and clarity drivers that align with segment's documented anxiety around ${input.research_question.toLowerCase().includes('insurance') ? 'premium increases' : 'perceived value'}. Evidence shows "concrete explanation" language pattern.`,
        evidence_cited: openTexts.slice(2, 5),
      }

    case 'COUNTERFACTUAL_RISK':
      return {
        pass_id: pass.pass_id,
        template: pass.template,
        predicted_winner: primaryWinner,
        predicted_distribution: baseDistribution,
        reasoning: `Counterfactual risk: ${input.stimuli[0]?.label} fails because it uses vague "market conditions" language which prior evidence shows erodes trust. ${input.stimuli[2]?.label ?? 'Option C'} fails on specificity. ${primaryWinner} avoids both failure modes.`,
        evidence_cited: openTexts.slice(0, 2),
        validation_flags: [`Validate ${primaryWinner} with recent-claimant segment specifically`],
      }

    case 'QUANTITATIVE_DIST':
      const quantDist: Record<string, number> = {}
      stimuliLabels.forEach((label, i) => {
        quantDist[label] = i === 0 ? 0.31 : i === 1 ? 0.56 : 0.13
      })
      return {
        pass_id: pass.pass_id,
        template: pass.template,
        predicted_winner: primaryWinner,
        predicted_distribution: quantDist,
        reasoning: `Quantitative distribution pass: ${primaryWinner} at ~56% preference share, ${input.stimuli[0]?.label} at ~31%, remainder split. Distribution is directional — validate before fielding.`,
        evidence_cited: [],
      }

    case 'VALIDATION_PLANNER':
      return {
        pass_id: pass.pass_id,
        template: pass.template,
        predicted_winner: primaryWinner,
        predicted_distribution: baseDistribution,
        reasoning: `Validation priority: Echo confidence is medium-high for general homeowners but low for recent claimants (only 12 relevant open-end responses). Recommend validating ${primaryWinner} with 40 homeowners + 20 recent claimants.`,
        evidence_cited: [],
        validation_flags: [
          `Recent claimants: 20 respondents needed (Wilson 32–58% — too wide)`,
          `General homeowners: 40 respondents needed (Wilson 72–86% — acceptable but confirm)`,
        ],
      }

    default:
      return {
        pass_id: pass.pass_id,
        template: pass.template,
        predicted_winner: primaryWinner,
        predicted_distribution: baseDistribution,
        reasoning: 'Demo pass result.',
        evidence_cited: [],
      }
  }
}
